import { LucideIcon } from 'lucide-react';
import { ReactNode, useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { HelpCircle } from 'lucide-react';
import { SHOW_CONTROL_CENTRE_INFO_TOOLTIPS } from './controlCentreUiFlags';

interface ControlCentreKPICardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: LucideIcon;
  variant?: 'primary' | 'secondary';
  colorScheme?: 'cyan' | 'blue' | 'green' | 'red' | 'amber' | 'gray';
  iconColor?: 'green' | 'amber' | 'blue' | 'red' | 'gray'; // For gray cards, specify icon color
  progressBar?: {
    value: number;
    color?: 'green' | 'yellow' | 'red';
  };
  tooltip?: ReactNode;
  className?: string;
  valueHoverScale?: number; // Custom scale for value on hover (default: 1.5)
  onClick?: () => void; // When set, card becomes clickable with visual indicator
  id?: string; // Optional id for scroll-to-KPI highlight
}

export default function ControlCentreKPICard({
  title,
  value,
  subtitle,
  icon: Icon,
  variant = 'primary',
  colorScheme = 'blue',
  iconColor,
  progressBar,
  tooltip,
  className = '',
  valueHoverScale = 1.5,
  onClick,
  id,
}: ControlCentreKPICardProps) {
  // Color mappings
  const colorMap = {
    cyan: {
      bg: 'from-chart-10/20 to-chart-10/10',
      border: 'border-chart-10/30',
      icon: 'text-chart-10',
      value: 'text-chart-10'
    },
    blue: {
      bg: 'from-chart-1/20 to-chart-1/10',
      border: 'border-chart-1/30',
      icon: 'text-chart-1',
      value: 'text-chart-1'
    },
    green: {
      bg: 'from-chart-3/20 to-chart-3/10',
      border: 'border-chart-3/30',
      icon: 'text-chart-3',
      value: 'text-chart-3'
    },
    red: {
      bg: 'from-chart-4/20 to-chart-4/10',
      border: 'border-chart-4/30',
      icon: 'text-chart-4',
      value: 'text-chart-4'
    },
    amber: {
      bg: 'from-chart-2/20 to-chart-2/10',
      border: 'border-chart-2/30',
      icon: 'text-chart-2',
      value: 'text-chart-2'
    },
    gray: {
      bg: 'bg-card/50',
      border: 'border-border',
      icon: 'text-muted-foreground',
      value: 'text-white'
    }
  };

  const colors = colorMap[colorScheme];
  const isPrimary = variant === 'primary';
  const isGray = colorScheme === 'gray';

  // Icon size classes - using full class names for Tailwind to detect them
  const iconSizeClass = isPrimary ? 'w-6 h-6' : 'w-5 h-5';
  const valueSizeClass = isPrimary ? 'text-4xl' : 'text-3xl';
  
  // Icon color - use custom iconColor for gray cards, otherwise use colorScheme
  const iconColorMap: Record<string, string> = {
    green: 'text-success',
    amber: 'text-warning',
    blue: 'text-ring',
    red: 'text-destructive',
    gray: 'text-muted-foreground'
  };
  const iconColorClass = isGray && iconColor
    ? iconColorMap[iconColor] || colors.icon
    : colors.icon;

  // Get shadow color based on color scheme
  const getShadowColor = () => {
    if (isGray) return 'rgb(var(--muted-foreground) / 0.8)';
    switch (colorScheme) {
      case 'cyan': return 'rgb(var(--chart-10) / 0.8)';
      case 'blue': return 'rgb(var(--chart-1) / 0.8)';
      case 'green': return 'rgb(var(--chart-3) / 0.8)';
      case 'red': return 'rgb(var(--chart-4) / 0.8)';
      case 'amber': return 'rgb(var(--chart-2) / 0.8)';
      default: return 'rgb(var(--chart-1) / 0.8)';
    }
  };

  // Get border hover color based on color scheme
  const getBorderHoverColor = () => {
    if (isGray) return 'border-border/50';
    switch (colorScheme) {
      case 'cyan': return 'hover:border-chart-10/50';
      case 'blue': return 'hover:border-chart-1/50';
      case 'green': return 'hover:border-chart-3/50';
      case 'red': return 'hover:border-chart-4/50';
      case 'amber': return 'hover:border-chart-2/50';
      default: return 'hover:border-chart-1/50';
    }
  };

  const shadowColor = getShadowColor();
  const borderHoverColor = getBorderHoverColor();
  const isClickable = Boolean(onClick);

  // Ref for the value element to control hover scale
  const valueRef = useRef<HTMLDivElement>(null);

  // Tooltip wrapper component that uses portal to avoid sidebar blocking
  const TooltipWrapper = ({ tooltip }: { tooltip: ReactNode }) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number } | null>(null);
    const iconRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      const handleMouseEnter = () => {
        if (iconRef.current) {
          const rect = iconRef.current.getBoundingClientRect();
          // Position tooltip above the icon, centered horizontally
          const padding = 12;
          const tooltipHeight = 200; // Estimated height for positioning
          
          // Center tooltip horizontally relative to icon
          const x = rect.left + rect.width / 2;
          
          // Position tooltip above the icon
          let y = rect.top - padding;
          
          // If tooltip would go off the top of the screen, position it below instead
          if (y - tooltipHeight < padding) {
            y = rect.bottom + padding;
          }
          
          setTooltipPosition({ x, y });
          setShowTooltip(true);
        }
      };

      const handleMouseLeave = () => {
        setShowTooltip(false);
        setTooltipPosition(null);
      };

      const iconElement = iconRef.current;
      if (iconElement) {
        iconElement.addEventListener('mouseenter', handleMouseEnter);
        iconElement.addEventListener('mouseleave', handleMouseLeave);
      }

      return () => {
        if (iconElement) {
          iconElement.removeEventListener('mouseenter', handleMouseEnter);
          iconElement.removeEventListener('mouseleave', handleMouseLeave);
        }
      };
    }, []);

    // Extract tooltip content - handle the nested structure from GPSTracking
    let tooltipContent: ReactNode = null;
    let iconElement: ReactNode = null;
    
    if (tooltip && typeof tooltip === 'object' && 'props' in tooltip) {
      const props = (tooltip as any).props;
      if (props?.children) {
        const children = Array.isArray(props.children) ? props.children : [props.children];
        // Find the HelpCircle icon (first child)
        iconElement = children.find((child: any) => 
          child?.type === HelpCircle || 
          (typeof child === 'object' && child?.props?.className?.includes('HelpCircle'))
        ) || children[0];
        // Find the tooltip content div (the absolute positioned div)
        const contentDiv = children.find((child: any) => 
          child?.props?.className?.includes('absolute')
        );
        if (contentDiv?.props?.children) {
          tooltipContent = contentDiv.props.children;
        }
      }
    }

    return (
      <>
        <div ref={iconRef} className="group/tooltip relative">
          {iconElement || <HelpCircle className="w-5 h-5 text-ring cursor-help hover:scale-110 transition-transform" />}
        </div>
        {showTooltip && tooltipPosition && createPortal(
          <div
            className="fixed px-3 py-2 bg-popover border border-ring/50 rounded-lg shadow-xl pointer-events-none z-float max-w-md max-h-screen-80 overflow-y-auto"
            style={{
              left: `${tooltipPosition.x}px`,
              top: `${tooltipPosition.y}px`,
              transform: 'translateX(-50%) translateY(-100%)'
            }}
          >
            {tooltipContent ? (
              tooltipContent
            ) : (
              <div>
                <div className="text-xs text-white font-semibold mb-2">Coverage Score Formula:</div>
                <div className="text-xs text-foreground space-y-1">
                  <div>• Base: 100 points</div>
                  <div>• <span className="text-destructive">-1.5</span> for every 1% of offline vehicles</div>
                  <div>• <span className="text-destructive">-2</span> for every alert per vehicle</div>
                  <div>• <span className="text-destructive">-1</span> for every 1% of missing position data</div>
                </div>
                <div className="text-xs text-muted-foreground mt-2 pt-2 border-t border-border">
                  Score = 100 - (Offline Rate × 1.5) - (Alerts/Vehicle × 2) - (Missing Positions Rate × 1)
                </div>
              </div>
            )}
          </div>,
          document.body
        )}
      </>
    );
  };

  return (
    <div
      id={id}
      onClick={onClick}
      className={`flex flex-col ${
        isGray
          ? `${colors.bg} rounded-lg ${colors.border} border`
          : `bg-gradient-to-br ${colors.bg} rounded-lg ${colors.border} border shadow-lg`
      } ${isPrimary ? 'p-6' : 'p-5'} ${className} ${isClickable ? `group transition-all duration-500 ease-in-out hover:-translate-y-1 ${borderHoverColor} cursor-pointer` : ''}`}
      style={{
        boxShadow: 'none',
        transition: 'all 0.5s ease-in-out'
      }}
      onMouseEnter={(e) => {
        if (!isClickable) return;
        if (!isGray) {
          e.currentTarget.style.boxShadow = `0 0 12px 2px ${shadowColor}`;
        }
        // Scale the value when hovering anywhere on the card
        if (valueRef.current) {
          valueRef.current.style.transform = `scale(${valueHoverScale})`;
        }
      }}
      onMouseLeave={(e) => {
        if (!isClickable) return;
        e.currentTarget.style.boxShadow = 'none';
        // Reset the value scale when leaving the card
        if (valueRef.current) {
          valueRef.current.style.transform = 'scale(1)';
        }
      }}
    >
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex items-start gap-2 min-w-0">
          <Icon className={`${iconSizeClass} ${iconColorClass} transition-transform duration-500 group-hover:scale-150 shrink-0 mt-0.5`} />
          <div className="min-w-0">
            <span className="block text-base font-medium text-foreground group-hover:text-white group-hover:drop-shadow-glow-neutral-md transition-all">
              {title}
            </span>
            {onClick && (
              <span className="block text-xs text-muted-foreground mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                Click to view
              </span>
            )}
          </div>
        </div>
        {SHOW_CONTROL_CENTRE_INFO_TOOLTIPS && tooltip && (
          <div className="flex items-center gap-2 shrink-0">
            <TooltipWrapper tooltip={tooltip} />
          </div>
        )}
      </div>
      
      <div className="flex-1 flex items-center">
        <div 
          ref={valueRef}
          className={`${valueSizeClass} font-bold ${colors.value} transition-transform duration-500 origin-left`}
        >
          {typeof value === 'number' ? value.toLocaleString() : value}
        </div>
      </div>

      <div className="mt-auto">
        {progressBar ? (
          <div className="w-full bg-secondary rounded-full h-2 mb-2">
            <div
              className={`h-2 rounded-full transition-all ${
                progressBar.color === 'green'
                  ? 'bg-success'
                  : progressBar.color === 'yellow'
                  ? 'bg-warning'
                  : 'bg-destructive'
              }`}
              style={{ width: `${Math.min(100, Math.max(0, progressBar.value))}%` }}
            />
          </div>
        ) : (
          <div className="w-full h-2 mb-2" aria-hidden />
        )}
        {subtitle && (
          <p className="text-xs text-muted-foreground">
            {typeof subtitle === 'string' 
              ? subtitle.charAt(0).toUpperCase() + subtitle.slice(1)
              : subtitle}
          </p>
        )}
      </div>
    </div>
  );
}
